import React from "react";
import { Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { BACKUP_STOCK_STATUS, coveredByBackup, getStockStatus, withBackup } from "../utils/stockUtils";

interface StockLevelBarProps {
  currentStock: number;
  maxStock: number;
  notifyLevel?: number | null; // New ratio-based system
  // Keep old props for backward compatibility
  criticalThreshold?: number;
  lowThreshold?: number;
  /**
   * BLU-1458: back-up stock the row can draw on (`backupFigure`). Above 0,
   * the bar and figure show own plus back-up (clamped at 0), orange when
   * back-up alone covers it, with a link mark and `backupLabel` (the split)
   * on hover and for screen readers.
   */
  backup?: number;
  backupLabel?: string | null;
}

const StockLevelBar: React.FC<StockLevelBarProps> = ({
  currentStock,
  maxStock,
  notifyLevel,
  criticalThreshold,
  lowThreshold,
  backup = 0,
  backupLabel,
}) => {
  const shownStock = withBackup(currentStock, backup);

  // Calculate percentage of stock
  const stockPercentage = Math.min((shownStock / maxStock) * 100, 100);

  // Get color based on ratio system if notifyLevel provided, otherwise fallback to absolute
  let barColor: string;

  if (coveredByBackup(currentStock, backup)) {
    barColor = BACKUP_STOCK_STATUS.barColor;
  } else if (notifyLevel !== undefined) {
    // Use new ratio-based system
    const { barColor: ratioColor } = getStockStatus(shownStock, notifyLevel);
    barColor = ratioColor;
  } else {
    // Fallback to old absolute system for backward compatibility
    barColor = "bg-[#A3D95A]"; // Default green

    if (criticalThreshold !== undefined && shownStock <= criticalThreshold) {
      barColor = "bg-red-500"; // Critical stock
    } else if (lowThreshold !== undefined && shownStock <= lowThreshold) {
      barColor = "bg-yellow-500"; // Low stock
    }
  }

  const showBackup = backup > 0 && !!backupLabel;

  return (
    <div className="flex items-center gap-2" title={showBackup ? backupLabel ?? undefined : undefined}>
      <div className="stock-bar-container">
        <div
          className={cn("stock-bar", barColor)}
          style={{ width: `${stockPercentage}%` }}
        />
      </div>
      <span className="text-sm font-medium w-10 text-right">{shownStock}</span>
      {showBackup && (
        <>
          <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
          <span className="sr-only"> {backupLabel}</span>
        </>
      )}
    </div>
  );
};

export default StockLevelBar;
